import { useState, useRef, useCallback, useEffect } from 'react';
import { uploadImage, reportRejectedUpload, UNCHECKED_IMAGE_MESSAGE } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import type { PreviewStyle } from '../contentSchemas';
import './ImageUploader.css';

// Per-plan image upload caps. Must match the server-side caps in
// lambda/routes/upload.ts MAX_IMAGE_SIZE_BY_PLAN. Client-side check is
// UX only — server is authoritative.
const MAX_IMAGE_BYTES_BY_PLAN: Record<string, number> = {
  free: 5 * 1024 * 1024,
  club: 5 * 1024 * 1024,
  pro:  50 * 1024 * 1024,
};

interface ImageUploaderProps {
  onUpload: (url: string) => void;
  currentImage?: string;
  label?: string;
  multiple?: boolean;
  /** When true, never show single-image preview — stay in upload-ready state (for gallery). */
  noPreview?: boolean;
  /** Shape the preview to match the actual website display. */
  previewStyle?: PreviewStyle;
  /** Called when upload starts/finishes so parent can disable Save/Publish buttons. */
  onUploadingChange?: (uploading: boolean) => void;
  /** Multiple mode: upload at most this many of the files picked. Extra files
   *  are never uploaded, so they never count against the club's storage. */
  maxFiles?: number;
}

function ImageUploader({
  onUpload,
  currentImage,
  label = 'Upload Image',
  multiple = false,
  noPreview = false,
  previewStyle,
  onUploadingChange,
  maxFiles,
}: ImageUploaderProps) {
  const { plan, clubId } = useAuth();
  // Explicit literal fallback keeps TS happy in strict mode (the record
  // index lookup widens to `number | undefined`).
  const maxBytes: number = MAX_IMAGE_BYTES_BY_PLAN[plan || 'free'] ?? 5 * 1024 * 1024;
  const maxMB = Math.round(maxBytes / (1024 * 1024));

  const [isUploading, setIsUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [error, setError] = useState<string>('');
  // Files whose upload failed, kept so "Try again" can retry without the
  // operator re-picking them. Non-empty === the last attempt did NOT save.
  const [failedFiles, setFailedFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const [isDragOver, setIsDragOver] = useState(false);

  // Sync preview when currentImage prop changes (e.g. opening a different item to edit)
  useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);
  const isDragging = useRef(false);
  const dragStart = useRef<{ x: number; y: number; posX: number; posY: number }>({ x: 0, y: 0, posX: 50, posY: 50 });
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Turn a raw upload error into advice the operator can act on. The generic
   *  "Upload failed" told them nothing, and (worse) the preview still showed
   *  the image, so a failed logo looked saved until it vanished on the site. */
  const suggestionFor = (err: unknown): string => {
    if (err instanceof Error && err.message === UNCHECKED_IMAGE_MESSAGE) return err.message;
    const msg = (err instanceof Error ? err.message : String(err || '')).toLowerCase();
    if (/413|too large|size|payload/.test(msg)) {
      return `The file may be too big. Try an image under ${maxMB}MB — resizing or saving as JPG usually does it.`;
    }
    if (/401|403|unauthor|session|token|expired/.test(msg)) {
      return 'Your session may have expired. Refresh the page, sign in again, then re-upload.';
    }
    if (/network|failed to fetch|timeout|connection|offline/.test(msg)) {
      return 'That looks like a connection problem. Check your internet and press Try again.';
    }
    return 'Press Try again. If it keeps failing, use a different file (PNG or JPG) or email hello@grassrootsfc.co.uk.';
  };

  const processFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (fileArray.length === 0) {
      setError('Please select image files');
      return;
    }

    const oversized = fileArray.filter(f => f.size > maxBytes);
    if (oversized.length > 0) {
      // Nudge non-Pro users toward the upgrade since this is a Pro-only lift.
      const hint = plan !== 'pro' ? ' Pro plan allows up to 50MB per image.' : '';
      setError(`${oversized.length} file(s) exceed ${maxMB}MB limit and were skipped.${hint}`);
      // Fire-and-forget beacon so ops can grep CloudWatch for these
      // rejections. Without this, the file is filtered client-side
      // and there's no record anywhere of what / who / when.
      oversized.forEach(f => {
        reportRejectedUpload({
          clubId: clubId || 'unknown',
          filename: f.name,
          size: f.size,
          mime: f.type,
          reason: 'oversized',
          plan: plan || 'unknown',
          cap: maxBytes,
        });
      });
    }

    let validFiles = fileArray.filter(f => f.size <= maxBytes);
    if (validFiles.length === 0) return;

    let tooMany = '';
    if (maxFiles !== undefined && validFiles.length > maxFiles) {
      tooMany = maxFiles > 0
        ? `Only ${maxFiles} more ${maxFiles === 1 ? 'photo fits' : 'photos fit'}, so the first ${maxFiles} ${maxFiles === 1 ? 'was' : 'were'} uploaded.`
        : 'There is no room for more photos.';
      validFiles = validFiles.slice(0, Math.max(0, maxFiles));
      if (validFiles.length === 0) { setError(tooMany); return; }
    }

    setError('');
    setFailedFiles([]);
    setIsUploading(true);
    onUploadingChange?.(true);
    setUploadCount(0);

    if (multiple || noPreview) {
      // Upload all files, calling onUpload as each completes
      const failed: File[] = [];
      for (const file of validFiles) {
        try {
          const url = await uploadImage(file);
          onUpload(url);
          setUploadCount(prev => prev + 1);
        } catch (err) {
          failed.push(file);
          setError(`${failed.length} photo(s) didn't upload and were NOT saved. ${suggestionFor(err)}`);
        }
      }
      setFailedFiles(failed);
      if (tooMany && failed.length === 0) setError(tooMany);
    } else {
      // Single file mode with preview
      const file = validFiles[0]!;
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
      setDragPos({ x: 50, y: 50 });

      try {
        const url = await uploadImage(file);
        onUpload(url);
      } catch (err) {
        // The local FileReader preview is showing the new image even though
        // nothing reached the server — that false "it worked" is how a logo
        // ends up missing on the live site. Put the preview back.
        setPreview(currentImage || null);
        setFailedFiles([file]);
        setError(`"${file.name}" didn't upload and was NOT saved. ${suggestionFor(err)}`);
      }
    }

    setIsUploading(false);
    onUploadingChange?.(false);
    setUploadCount(0);
    // Reset file input so same files can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRetry = async () => {
    if (failedFiles.length === 0 || isUploading) return;
    await processFiles(failedFiles);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await processFiles(files);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      await processFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreview(null);
    onUpload('');
    setDragPos({ x: 50, y: 50 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /* ── Drag-to-reposition for hero/photo previews ── */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!previewStyle) return;
    e.preventDefault();
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, posX: dragPos.x, posY: dragPos.y };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Convert pixel movement to percentage shift
      const dx = ((ev.clientX - dragStart.current.x) / rect.width) * 100;
      const dy = ((ev.clientY - dragStart.current.y) / rect.height) * 100;
      // Invert: dragging right moves the viewport left → object-position decreases
      const newX = Math.max(0, Math.min(100, dragStart.current.posX - dx));
      const newY = Math.max(0, Math.min(100, dragStart.current.posY - dy));
      setDragPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [previewStyle, dragPos]);

  /* ── Preview container class ── */
  const previewContainerClass = previewStyle
    ? `image-preview-container preview-${previewStyle}`
    : 'image-preview-container';

  const objectPosition = previewStyle
    ? `${dragPos.x}% ${dragPos.y}%`
    : undefined;

  return (
    <div className="image-uploader">
      <label className="uploader-label">{label}</label>

      {preview && !noPreview ? (
        <div className={previewContainerClass} ref={containerRef}>
          {isUploading && (
            <div className="upload-overlay">
              <div className="upload-overlay-spinner" />
              <span>Uploading…</span>
            </div>
          )}
          {previewStyle && (
            <p className="preview-label">
              {previewStyle === 'hero' ? 'Cover photo preview' : 'Photo preview'}
              {' \u2014 drag to reposition'}
            </p>
          )}
          <div
            className={previewStyle ? `preview-frame preview-frame-${previewStyle}` : 'preview-frame-default'}
            onMouseDown={previewStyle ? handleMouseDown : undefined}
            style={previewStyle ? { cursor: 'grab' } : undefined}
          >
            <img
              src={preview}
              alt="Preview"
              className={previewStyle ? 'preview-cover-img' : 'image-preview'}
              style={objectPosition ? { objectPosition } : undefined}
              draggable={false}
            />
            {previewStyle === 'hero' && (
              <div className="preview-hero-identity-bar">
                <div className="preview-hero-badge-circle" />
                <div className="preview-hero-name-placeholder">
                  <div className="ph-line ph-club-name" />
                </div>
              </div>
            )}
          </div>
          <div className="image-actions">
            <button
              type="button"
              onClick={handleClick}
              className="btn btn-secondary btn-small"
              disabled={isUploading}
            >
              Change Image
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="btn btn-danger btn-small"
              disabled={isUploading}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`upload-area ${isDragOver ? 'upload-area-dragover' : ''}`}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="upload-icon">📷</div>
          <p className="upload-text">
            {isUploading
              ? (multiple || noPreview)
                ? `Uploading${uploadCount > 0 ? ` (${uploadCount} done)` : ''}...`
                : 'Uploading...'
              : (multiple || noPreview)
                ? 'Click or drag photos here'
                : 'Click to upload image'}
          </p>
          <p className="upload-hint">{(multiple || noPreview) ? `Select multiple files or drag & drop • PNG, JPG up to ${maxMB}MB each` : `PNG, JPG up to ${maxMB}MB`}</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleFileSelect}
        className="file-input"
        disabled={isUploading}
      />

      {error && (
        failedFiles.length > 0 ? (
          <div className="upload-failed" role="alert">
            <div className="upload-failed-head">
              <span className="upload-failed-icon" aria-hidden="true">⚠️</span>
              <span className="upload-failed-title">Upload failed — nothing was saved</span>
            </div>
            <p className="upload-failed-msg">{error}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="btn btn-primary btn-small"
              disabled={isUploading}
            >
              {isUploading ? 'Retrying…' : 'Try again'}
            </button>
          </div>
        ) : (
          <p className="form-error">{error}</p>
        )
      )}
    </div>
  );
}

export default ImageUploader;
